import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { atomWithDebounce } from '@/atoms/debounce';
import { playerMetricsAtom } from '@/atoms/metric/playerMetrics';
import { isCompetitionAverageModeAtom } from '@/atoms/queryParams';
import { playersLeagueAverageNormalisedAggregatesUrl } from '@/query/url';
import { Nullable } from '@/types/generic';
import { NestedObject } from '@/types/object';
import { hasEmptyInFilters } from '@/utils/api';
import { unwrapWithLoading } from '@/utils/jotai';
import { objSnakeToCamel } from '@/utils/queries';
import { fetchClientAtom } from '../client';
import { radarDataAtom } from '../radar';
import { playerRadarTableColumnsAtom } from '../tableColumns';
import { playerRadarTemplatesAtom } from '../userConfigs';
import {
  playerComparisonRadarDataAtom,
  unwrappedPlayerComparisonMinutesPlayed360Atom,
  unwrappedPlayerComparisonMinutesPlayedAtom,
} from './playerComparisonRadar';

export const playerLeagueAverageRadarFilterDebounceObject = atomWithDebounce<Nullable<NestedObject>>(null);
export const { debouncedValueAtom: playerLeagueAverageRadarFilterAtom } = playerLeagueAverageRadarFilterDebounceObject;

const rawPlayerLeagueAverageRadarDataAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const playerLeagueAverageFilterParams = get(playerLeagueAverageRadarFilterAtom);

  const queryKey = ['playerLeagueAverageRadarData', playerLeagueAverageFilterParams] as const;

  const queryFn = async ({ queryKey: [, filterParams] }: { queryKey: typeof queryKey }) => {
    if (hasEmptyInFilters(filterParams)) return {} satisfies Record<string, number>;

    // API returns a flat { [snake_case_metric_key]: number } record
    return (await fetch(playersLeagueAverageNormalisedAggregatesUrl(filterParams))) as Record<string, number>;
  };

  return { queryKey, queryFn };
});

export const playerLeagueAverageRadarDataAtom = unwrapWithLoading(
  radarDataAtom(
    rawPlayerLeagueAverageRadarDataAtom,
    playerMetricsAtom,
    playerRadarTemplatesAtom,
    playerRadarTableColumnsAtom,
  ),
  [],
);

export const playerLeagueAverageIsLoadingAtom = atom(get => {
  if (!get(isCompetitionAverageModeAtom)) return false;
  const { isLoading: radarIsLoading } = get(playerLeagueAverageRadarDataAtom);
  return radarIsLoading;
});

export const playerRenderedComparisonSourceAtom = atom(get =>
  get(isCompetitionAverageModeAtom) ? get(playerLeagueAverageRadarDataAtom) : get(playerComparisonRadarDataAtom),
);

const playerLeagueAverageAggregatesDataAtom = atom(async get => {
  // KeysToCamelCase<Record<string, number>> is structurally Record<string, number> for generic string keys
  const playerLeagueAverageRadarData = objSnakeToCamel((await get(rawPlayerLeagueAverageRadarDataAtom)).data) as Record<
    string,
    number
  >;

  return {
    minutesPlayed: playerLeagueAverageRadarData.minutesPlayed ?? 0,
    minutesPlayed360: playerLeagueAverageRadarData.minutesPlayed360 ?? 0,
  };
});

const playerLeagueAverageMinutesPlayedAtom = atom(
  async get => (await get(playerLeagueAverageAggregatesDataAtom)).minutesPlayed,
);
export const unwrappedPlayerLeagueAverageMinutesPlayedAtom = unwrap(
  playerLeagueAverageMinutesPlayedAtom,
  prev => prev ?? 0,
);

const playerLeagueAverageMinutesPlayed360Atom = atom(
  async get => (await get(playerLeagueAverageAggregatesDataAtom)).minutesPlayed360,
);
export const unwrappedPlayerLeagueAverageMinutesPlayed360Atom = unwrap(
  playerLeagueAverageMinutesPlayed360Atom,
  prev => prev ?? 0,
);

export const unwrappedPlayerRenderedComparisonMinutesPlayedAtom = atom(get =>
  get(isCompetitionAverageModeAtom)
    ? get(unwrappedPlayerLeagueAverageMinutesPlayedAtom)
    : get(unwrappedPlayerComparisonMinutesPlayedAtom),
);

export const unwrappedPlayerRenderedComparisonMinutesPlayed360Atom = atom(get =>
  get(isCompetitionAverageModeAtom)
    ? get(unwrappedPlayerLeagueAverageMinutesPlayed360Atom)
    : get(unwrappedPlayerComparisonMinutesPlayed360Atom),
);
